/**
 * CONTRACT.md §2.13 — charts, and the accessibility invariant that licenses the
 * palette.
 *
 * **Every chart carries `role="img"`, an `aria-label` that states the
 * conclusion, and a `<details class="tableview">` table fallback. No
 * exceptions.** `Chart` takes `conclusion` and `table` as required props, so a
 * chart without either is a type error before it is a review comment;
 * `expectChartsAccessible` in `src/test/contract.ts` is the runtime half, for
 * pages.
 *
 * ## Why there is no Recharts here
 *
 * `ARCHITECTURE.md` §Frontend and `.docs/09-design-system.md` both name Recharts.
 * It is not used, and the session file allows the swap ("hand-written SVG is
 * acceptable … if Recharts cannot match the mockup's look — say which in the
 * report"). Three reasons, all of them about this contract rather than about the
 * library:
 *
 *   1. **The class check.** Recharts emits `.recharts-wrapper`,
 *      `.recharts-surface` and a `recharts-layer` per element. CONTRACT.md §4.8
 *      is checked mechanically in both directions, and the only way to keep a
 *      Recharts chart green is to teach the checker to ignore a prefix — which
 *      is the check being switched off one library at a time.
 *   2. **`.chart` is a `viewBox`.** `width:100%; height:auto` is how every
 *      mockup chart scales. Recharts sizes an `<svg>` in pixels and needs a
 *      `ResponsiveContainer` with a `ResizeObserver` to do the same, which jsdom
 *      does not have — every chart test would be testing a zero-width chart.
 *   3. **The mockups are already SVG.** All thirteen of them, with direct value
 *      labels, threshold rules and a `<title>` per bar. Reproducing that through
 *      a chart library's escape hatches is more code than the four shapes below.
 *
 * `.docs/reports/09-report.md` records this as a deviation, and what would have
 * to be true to revisit it.
 *
 * ## The four shapes
 *
 * `ColumnChart` (vertical, stackable), `BarChart` (horizontal, direct-labelled),
 * `LineChart` (a series over time, optionally filled) and `ProportionBar` (one
 * bar, parts of a whole) are every chart in `mockups/**`. Each takes data and
 * the strings a page has already formatted through `useFormat()` — a chart never
 * formats a number, which is why `title` and `valueLabel` are props.
 */

import type { ReactNode } from 'react'

import { cx, type Common } from './common.js'

/* ------------------------------------------------------------ the palette */

/**
 * `--c1..--c4` categorical, in order and never cycled; `--s1..--s7` sequential.
 *
 * `demph` and `need` are not categories. `demph` is the rest of a series when one
 * mark is the point; `need` is `--accent-ochre`, and it means on a chart exactly
 * what DESIGN.md P4 says it means everywhere else — **this needs your decision, or
 * this costs you more**. It is a member rather than a fifth category precisely so
 * that a chart which wants to say that does not reach for `c1` and quietly spend the
 * attention colour on a category, which is the bug session 24 found: `--c1` used to
 * be the ochre hex, so every series 1 in the product was shouting.
 */
export type ChartSlot =
  'c1' | 'c2' | 'c3' | 'c4' | 's1' | 's2' | 's3' | 's4' | 's5' | 's6' | 's7' | 'demph' | 'need'

export function slotColor(slot: ChartSlot): string {
  return `var(--${slot})`
}

/**
 * An empty chart's own height, in the units of whatever `viewBox` it was given.
 * `.chart` is `width:100%; height:auto`, so the aspect ratio of the box is the
 * height on the page — and a chart with nothing in it should not hold four
 * hundred pixels open above the line that says so.
 */
const EMPTY_VIEW_HEIGHT = 24

/** The same box, flattened: same origin, same width, almost no height. */
function flattened(viewBox: string): string {
  const [x = '0', y = '0', width = '0'] = viewBox.trim().split(/\s+/)
  return `${x} ${y} ${width} ${String(EMPTY_VIEW_HEIGHT)}`
}

/** Coordinates, trimmed. `42`, `86.5` — not `86.49999999999999`. */
function n(value: number): string {
  return String(Number(value.toFixed(2)))
}

/* -------------------------------------------------------------- the wrapper */

export type ChartColumn = {
  readonly label: string
  /** `.right` — a column of figures. */
  readonly right?: boolean
}

export type ChartTable = {
  readonly columns: readonly ChartColumn[]
  /** One array per row. `Num`, `DateText` and plain strings all belong here. */
  readonly rows: readonly (readonly ReactNode[])[]
  /** §2.9: usually wrong. Give the chart a caption **or** a note, never both. */
  readonly caption?: string
}

export type ChartProps = Common & {
  /** What the chart says, in a sentence. Becomes the `aria-label`. */
  readonly conclusion: string
  /** The numbers, for a screen reader and for anyone who wants them. */
  readonly table: ChartTable
  readonly viewBox: string
  /** A `Legend`, when the series are not directly labelled. */
  readonly legend?: ReactNode | undefined
  /** The one narration line under the chart — `<p class="small muted">`. */
  readonly note?: ReactNode | undefined
  readonly children: ReactNode
}

/**
 * The `stack stack--tight` wrapper CONTRACT.md §2.13 specifies, holding the
 * svg, the narration line and the table view — "`<figure>`/`<figcaption>` are
 * also safe … but the stack is what the pages use, because it puts the parts on
 * the `--sp-2` rhythm without a second decision."
 */
export function Chart({
  conclusion,
  table,
  viewBox,
  legend,
  note,
  children,
  ...common
}: ChartProps) {
  const empty = table.rows.length === 0

  if (import.meta.env.DEV && note !== undefined && table.caption !== undefined) {
    throw new Error(
      'A chart gets either a caption or a commentary line, never both (CONTRACT.md §2.13).',
    )
  }

  return (
    <div className="stack stack--tight" {...common}>
      <svg
        className="chart"
        viewBox={empty ? flattened(viewBox) : viewBox}
        role="img"
        aria-label={empty ? `${conclusion} There is nothing to chart yet.` : conclusion}
      >
        {empty ? null : children}
      </svg>
      {legend}
      {empty ? <p className="empty">Nothing to chart for this period.</p> : null}
      {note === undefined ? null : <p className="small muted">{note}</p>}
      <TableView {...table} />
    </div>
  )
}

export type TableViewProps = ChartTable & {
  /** The summary line. "Table view" everywhere in the mockups. */
  readonly summary?: string | undefined
}

/** The fallback table. Styled the same as `Fold` — same marker, same rank. */
export function TableView({ columns, rows, caption, summary }: TableViewProps) {
  return (
    <details className="tableview">
      <summary>{summary ?? 'Table view'}</summary>
      <table className="table">
        {caption === undefined ? null : <caption>{caption}</caption>}
        <thead>
          <tr>
            {columns.map((column) => (
              <th className={column.right === true ? 'right' : undefined} key={column.label}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td
                  className={columns[cellIndex]?.right === true ? 'right num' : undefined}
                  key={cellIndex}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

/* --------------------------------------------------------------- the legend */

export type LegendProps = Common & { readonly children: ReactNode }

export function Legend({ children, ...common }: LegendProps) {
  return (
    <div className="legend" {...common}>
      {children}
    </div>
  )
}

export type LegendKeyProps = Common & {
  readonly slot: ChartSlot
  readonly children: ReactNode
}

/** The swatch is §4.1's "matching `.legend__sw` background" — a sanctioned inline value. */
export function LegendKey({ slot, children, ...common }: LegendKeyProps) {
  return (
    <span className="legend__key" {...common}>
      <span className="legend__sw" style={{ background: slotColor(slot) }} />
      {children}
    </span>
  )
}

/* ------------------------------------------------------------- the sparkline */

export type SparklineProps = {
  readonly points: readonly number[]
  /** The last segment and its dot in `--c1`, as every dashboard tile draws it. */
  readonly highlightLast?: boolean | undefined
  /** What the line says. Without it there is no `role="img"` worth having. */
  readonly label: string
  readonly width?: number | undefined
  readonly height?: number | undefined
}

const SPARK_PAD = 3

/**
 * The 120×30 line in a `StatSpark`.
 *
 * **Fewer than two points renders nothing at all** — not an empty svg with a
 * `role="img"`, which would announce a chart that is not there. One reading is
 * a number, and the tile is already showing it.
 */
export function Sparkline({ points, highlightLast, label, width, height }: SparklineProps) {
  const w = width ?? 120
  const h = height ?? 30
  if (points.length < 2) return null

  const low = Math.min(...points)
  const high = Math.max(...points)
  const span = high - low === 0 ? 1 : high - low
  const step = (w - SPARK_PAD * 2) / (points.length - 1)

  const coords = points.map((value, index) => ({
    x: SPARK_PAD + step * index,
    y: h - SPARK_PAD - ((value - low) / span) * (h - SPARK_PAD * 2),
  }))
  const at = (index: number) => {
    const point = coords[index]
    return point === undefined ? '' : `${n(point.x)},${n(point.y)}`
  }

  const head = highlightLast === true ? coords.slice(0, -1) : coords
  const last = coords[coords.length - 1]

  return (
    <svg width={w} height={h} viewBox={`0 0 ${n(w)} ${n(h)}`} role="img" aria-label={label}>
      <polyline
        fill="none"
        stroke={slotColor('demph')}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={head.map((point) => `${n(point.x)},${n(point.y)}`).join(' ')}
      />
      {highlightLast === true && last !== undefined ? (
        <>
          <polyline
            fill="none"
            stroke={slotColor('c1')}
            strokeWidth="2"
            strokeLinecap="round"
            points={`${at(coords.length - 2)} ${at(coords.length - 1)}`}
          />
          <circle
            cx={n(last.x)}
            cy={n(last.y)}
            r="4"
            fill={slotColor('c1')}
            stroke="var(--surface)"
            strokeWidth="2"
          />
        </>
      ) : null}
    </svg>
  )
}

/* ----------------------------------------------------------------- the axes */

export type AxisTick = { readonly value: number; readonly label: string }

/**
 * Which tick carries `.axisline` rather than `.gridline` — the lowest one.
 *
 * By value, not by position. Ticks are written top-first everywhere in this
 * repository because that is the order they are read in, and a positional test
 * puts the heavy rule across the top of the plot: `report-2026-08.html` has
 * three `gridline`s and one `axisline`, and the `axisline` is the zero.
 */
function isBaseline(tick: AxisTick, ticks: readonly AxisTick[]): boolean {
  return tick.value === Math.min(...ticks.map((each) => each.value))
}

export type Threshold = {
  readonly value: number
  /** The word beside the rule — "work starts", "daily average 53", "your 4.0× target". */
  readonly label?: string
}

/* --------------------------------------------------------- the column chart */

export type ColumnDatum = {
  /** The x-axis label under the column. */
  readonly label: string
  /** One number, or one per stacked segment in `slots` order. */
  readonly value: number | readonly number[]
  /** The `<title>` a pointer reveals — already formatted by the page. */
  readonly title: string
}

export type ColumnChartProps = {
  readonly data: readonly ColumnDatum[]
  readonly ticks: readonly AxisTick[]
  /** The top of the y axis. Defaults to the largest tick. */
  readonly max?: number | undefined
  /** One slot per stacked segment. A single-value column uses the first. */
  readonly slots?: readonly ChartSlot[] | undefined
  /** A horizontal rule across the plot — an average, a floor. */
  readonly threshold?: Threshold | undefined
  /** Leaves out the x labels, for a series too dense to label (24 days). */
  readonly labelEvery?: number | undefined
}

/**
 * The plot's own box. `left` is the y-axis gutter and it is **52**, which is
 * `mockups/studio/analytics.html`'s own: its labels end at 46 and its plot starts at
 * 52, because `$10,000` at `.ax`'s size is about forty units wide.
 *
 * It was 34, so a label ended at 28 and ran off the left edge of the viewBox. The
 * studio analytics screenshots showed both column charts publishing their y-axis as
 * `,000` — a number nobody can read, on the two charts that are about money.
 */
const COLUMN_BOX = { width: 660, height: 210, left: 52, right: 640, top: 20, base: 160 }
const COLUMN_RADIUS = 3

/** `viewBox` for a `ColumnChart`, so the page and the chart cannot disagree. */
export const COLUMN_VIEW_BOX = `0 0 ${n(COLUMN_BOX.width)} ${n(COLUMN_BOX.height)}`

export function ColumnChart({ data, ticks, max, slots, threshold, labelEvery }: ColumnChartProps) {
  const palette = slots ?? ['c3']
  const top = max ?? Math.max(...ticks.map((tick) => tick.value), 0)
  const plot = COLUMN_BOX.base - COLUMN_BOX.top
  const y = (value: number) => COLUMN_BOX.base - (value / (top === 0 ? 1 : top)) * plot

  const span = (COLUMN_BOX.right - COLUMN_BOX.left) / Math.max(data.length, 1)
  const barWidth = Math.max(span * 0.64, 2)
  const every = labelEvery ?? 1

  return (
    <>
      {ticks.map((tick) => (
        <g key={tick.label}>
          <line
            className={isBaseline(tick, ticks) ? 'axisline' : 'gridline'}
            x1={COLUMN_BOX.left}
            y1={n(y(tick.value))}
            x2={COLUMN_BOX.right}
            y2={n(y(tick.value))}
          />
          <text className="ax" x={COLUMN_BOX.left - 6} y={n(y(tick.value) + 4)} textAnchor="end">
            {tick.label}
          </text>
        </g>
      ))}

      {data.map((datum, index) => {
        const x = COLUMN_BOX.left + span * index + (span - barWidth) / 2
        const values = typeof datum.value === 'number' ? [datum.value] : datum.value
        let bottom = COLUMN_BOX.base
        const segments = values.map((value, segment) => {
          const height = (value / (top === 0 ? 1 : top)) * plot
          const segmentTop = bottom - height
          const rounded = segment === values.length - 1
          const path = roundedColumn(x, segmentTop, barWidth, bottom, rounded)
          bottom = segmentTop
          return { path, slot: palette[segment] ?? palette[0] ?? 'c3' }
        })

        return (
          <g key={datum.label}>
            {segments.map((segment, segmentIndex) => (
              <path key={segmentIndex} d={segment.path} fill={slotColor(segment.slot)}>
                <title>{datum.title}</title>
              </path>
            ))}
            {index % every === 0 ? (
              <text
                className="ax"
                x={n(x + barWidth / 2)}
                y={COLUMN_BOX.base + 18}
                textAnchor="middle"
              >
                {datum.label}
              </text>
            ) : null}
          </g>
        )
      })}

      {threshold === undefined ? null : (
        <>
          <line
            className="thresh"
            x1={COLUMN_BOX.left}
            y1={n(y(threshold.value))}
            x2={COLUMN_BOX.right}
            y2={n(y(threshold.value))}
          />
          {threshold.label === undefined ? null : (
            /*
             * At the top of the plot, not beside the rule. `widget-chatbot.html`
             * puts "daily average 53" at `y=14` with its line at `y=67`, and the
             * reason is visible the moment it is not: a label on the rule sits
             * on top of whichever column happens to reach it.
             */
            <text className="ax" x={COLUMN_BOX.left} y={COLUMN_BOX.top - 6}>
              {threshold.label}
            </text>
          )}
        </>
      )}
    </>
  )
}

/** The mockups' bar: square at the base, `COLUMN_RADIUS` at the top, or square if stacked under. */
function roundedColumn(x: number, top: number, width: number, base: number, round: boolean) {
  if (!round || base - top < COLUMN_RADIUS) {
    return `M${n(x)} ${n(top)} H${n(x + width)} V${n(base)} H${n(x)} Z`
  }
  const r = COLUMN_RADIUS
  return [
    `M${n(x)} ${n(top + r)}`,
    `Q${n(x)} ${n(top)} ${n(x + r)} ${n(top)}`,
    `H${n(x + width - r)}`,
    `Q${n(x + width)} ${n(top)} ${n(x + width)} ${n(top + r)}`,
    `V${n(base)}`,
    `H${n(x)}`,
    'Z',
  ].join(' ')
}

/* ------------------------------------------------------------ the bar chart */

export type BarDatum = {
  /** The category, printed to the left of its bar. */
  readonly label: string
  readonly value: number
  readonly title: string
  /** The figure printed at the end of the bar — `68,200`, `9.64×`. */
  readonly valueLabel: string
  readonly slot?: ChartSlot
}

export type BarChartProps = {
  readonly data: readonly BarDatum[]
  readonly max?: number | undefined
  /** A vertical rule across the bars — the target, the floor. */
  readonly threshold?: Threshold | undefined
  /** How much room the category labels need. 120 in the mockups, 150 for long ones. */
  readonly labelWidth?: number | undefined
}

const BAR_BOX = { width: 620, top: 8, rowHeight: 46, barHeight: 16, valueGap: 14 }
const BAR_RADIUS = 4

/** `viewBox` for a `BarChart` of `count` bars. */
export function barViewBox(count: number): string {
  return `0 0 ${n(BAR_BOX.width)} ${n(BAR_BOX.top + BAR_BOX.rowHeight * Math.max(count, 1))}`
}

export function BarChart({ data, max, threshold, labelWidth }: BarChartProps) {
  const axis = labelWidth ?? 120
  const asked = max ?? Math.max(...data.map((datum) => datum.value), threshold?.value ?? 0, 1)
  /*
   * A caller that computes its own `max` over a quiet period passes 0, and every
   * path comes out `MNaN NaN H…` — which renders nothing and logs nothing. The
   * other two shapes in this file guard the same division the same way.
   */
  const top = asked === 0 ? 1 : asked
  /* The value label sits past the bar, so the plot stops short of the right edge. */
  const plot = BAR_BOX.width - axis - 90
  const x = (value: number) => axis + (value / top) * plot
  const bottom = BAR_BOX.top + BAR_BOX.rowHeight * Math.max(data.length, 1) - 28

  return (
    <>
      <line className="axisline" x1={axis} y1={BAR_BOX.top} x2={axis} y2={n(bottom)} />
      {data.map((datum, index) => {
        const rowTop = BAR_BOX.top + BAR_BOX.rowHeight * index + 6
        const middle = rowTop + BAR_BOX.barHeight / 2 + 4
        return (
          <g key={datum.label}>
            <text className="ax" x={axis - 8} y={n(middle)} textAnchor="end">
              {datum.label}
            </text>
            <path
              d={roundedBar(axis, rowTop, x(datum.value), BAR_BOX.barHeight)}
              fill={slotColor(datum.slot ?? 'c1')}
            >
              <title>{datum.title}</title>
            </path>
            <text className="lbl" x={n(x(datum.value) + BAR_BOX.valueGap)} y={n(middle)}>
              {datum.valueLabel}
            </text>
          </g>
        )
      })}
      {threshold === undefined ? null : (
        <line
          className="thresh"
          x1={n(x(threshold.value))}
          y1={BAR_BOX.top}
          x2={n(x(threshold.value))}
          y2={n(bottom)}
        />
      )}
    </>
  )
}

/** Square at the axis, `BAR_RADIUS` at the far end — the mockups' `q4 0 4 4`. */
function roundedBar(left: number, top: number, right: number, height: number) {
  if (right - left < BAR_RADIUS) {
    return `M${n(left)} ${n(top)} H${n(right)} V${n(top + height)} H${n(left)} Z`
  }
  const r = n(BAR_RADIUS)
  return [
    `M${n(left)} ${n(top)}`,
    `H${n(right - BAR_RADIUS)}`,
    `q${r} 0 ${r} ${r}`,
    `V${n(top + height - BAR_RADIUS)}`,
    `q0 ${r} ${n(-BAR_RADIUS)} ${r}`,
    `H${n(left)}`,
    'Z',
  ].join(' ')
}

/* ----------------------------------------------------------- the line chart */

export type LinePoint = {
  readonly label: string
  readonly value: number
  readonly title?: string
}

export type LineChartProps = {
  readonly points: readonly LinePoint[]
  readonly ticks: readonly AxisTick[]
  readonly max?: number | undefined
  readonly slot?: ChartSlot | undefined
  /** Fills under the line at 10% — what `report-2026-08.html` does. */
  readonly area?: boolean | undefined
  /** The figure printed at the last point. */
  readonly endLabel?: string | undefined
  /**
   * A second figure annotated on an earlier point — `report-2026-08.html`'s muted
   * `6,120` beside January, the reading the whole line is measured against.
   *
   * It is drawn with `.ax` rather than `.lbl`, which is the class equivalent of the
   * mockup's own `class="lbl" style="fill:var(--t-muted)"`: same mono face, same
   * `--fs-1`, muted fill, and no inline style for `expectNoInlineStyle` to find. The
   * emphasis belongs to the end label — the figure the chart is about.
   */
  readonly startLabel?: { readonly at: number; readonly text: string } | undefined
  /** A vertical rule at one point — "work starts". */
  readonly marker?: { readonly at: number; readonly label: string } | undefined
}

const LINE_BOX = { width: 660, height: 232, left: 46, right: 640, top: 16, base: 180 }

export const LINE_VIEW_BOX = `0 0 ${n(LINE_BOX.width)} ${n(LINE_BOX.height)}`

export function LineChart({
  points,
  ticks,
  max,
  slot,
  area,
  endLabel,
  startLabel,
  marker,
}: LineChartProps) {
  const colour = slotColor(slot ?? 'c3')
  const top = max ?? Math.max(...ticks.map((tick) => tick.value), 0)
  const plot = LINE_BOX.base - LINE_BOX.top
  const step = (LINE_BOX.right - LINE_BOX.left) / Math.max(points.length - 1, 1)

  const coords = points.map((point, index) => ({
    x: LINE_BOX.left + step * index,
    y: LINE_BOX.base - (point.value / (top === 0 ? 1 : top)) * plot,
  }))
  const polyline = coords.map((point) => `${n(point.x)},${n(point.y)}`).join(' ')
  const last = coords[coords.length - 1]

  return (
    <>
      {ticks.map((tick) => {
        const y = LINE_BOX.base - (tick.value / (top === 0 ? 1 : top)) * plot
        return (
          <g key={tick.label}>
            <line
              className={isBaseline(tick, ticks) ? 'axisline' : 'gridline'}
              x1={LINE_BOX.left}
              y1={n(y)}
              x2={LINE_BOX.right}
              y2={n(y)}
            />
            <text className="ax" x={LINE_BOX.left - 6} y={n(y + 4)} textAnchor="end">
              {tick.label}
            </text>
          </g>
        )
      })}

      {area === true && coords.length > 1 ? (
        <path d={areaUnder(coords)} fill={colour} opacity=".10" />
      ) : null}

      <polyline
        fill="none"
        stroke={colour}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={polyline}
      />

      {last === undefined ? null : (
        <circle
          cx={n(last.x)}
          cy={n(last.y)}
          r="4.5"
          fill={colour}
          stroke="var(--surface)"
          strokeWidth="2"
        />
      )}
      {endLabel === undefined || last === undefined ? null : (
        <text className="lbl" x={n(last.x - 8)} y={n(last.y - 10)} textAnchor="end">
          {endLabel}
        </text>
      )}

      {startLabel === undefined || coords[startLabel.at] === undefined ? null : (
        <text
          className="ax"
          x={n((coords[startLabel.at]?.x ?? 0) + 6)}
          y={n((coords[startLabel.at]?.y ?? 0) - 7)}
        >
          {startLabel.text}
        </text>
      )}

      {points.map((point, index) => (
        <text
          className="ax"
          key={point.label}
          x={n(LINE_BOX.left + step * index)}
          y={LINE_BOX.base + 18}
          textAnchor="middle"
        >
          {point.label}
        </text>
      ))}

      {marker === undefined ? null : (
        <>
          <line
            className="thresh"
            x1={n(LINE_BOX.left + step * marker.at)}
            y1={LINE_BOX.top}
            x2={n(LINE_BOX.left + step * marker.at)}
            y2={LINE_BOX.base}
          />
          <text className="ax" x={n(LINE_BOX.left + step * marker.at + 5)} y={LINE_BOX.top + 12}>
            {marker.label}
          </text>
        </>
      )}
    </>
  )
}

/** The line, closed down to the baseline and back — the 10% fill under a series. */
function areaUnder(coords: readonly { readonly x: number; readonly y: number }[]): string {
  const line = coords.map((point, index) => `${index === 0 ? 'M' : 'L'}${n(point.x)} ${n(point.y)}`)
  return [
    ...line,
    `L${n(LINE_BOX.right)} ${n(LINE_BOX.base)}`,
    `L${n(LINE_BOX.left)} ${n(LINE_BOX.base)}`,
    'Z',
  ].join(' ')
}

/* -------------------------------------------------------- the proportion bar */

export type ProportionPart = {
  readonly label: string
  readonly value: number
  readonly title: string
  /** Printed inside the segment when it is wide enough — "Retainer · 65.0%". */
  readonly inside?: string
  readonly slot: ChartSlot
}

export type ProportionBarProps = {
  readonly parts: readonly ProportionPart[]
  /** The left and right ends of the axis under the bar — `$0` and `$14,998`. */
  readonly startLabel: string
  readonly endLabel: string
}

const PROP_BOX = { width: 640, height: 118, left: 40, right: 600, top: 20, bar: 26 }
/** Below this many pixels a label does not fit inside its own segment. */
const PROP_LABEL_FLOOR = 56

export const PROPORTION_VIEW_BOX = `0 0 ${n(PROP_BOX.width)} ${n(PROP_BOX.height)}`

export function ProportionBar({ parts, startLabel, endLabel }: ProportionBarProps) {
  const total = parts.reduce((sum, part) => sum + part.value, 0)
  const plot = PROP_BOX.right - PROP_BOX.left
  let x = PROP_BOX.left

  const drawn = parts.map((part, index) => {
    const width = total === 0 ? 0 : (part.value / total) * plot
    const at = x
    x += width
    return { part, at, width, last: index === parts.length - 1 }
  })

  return (
    <>
      {drawn.map(({ part, at, width, last }) =>
        last ? (
          <path
            key={part.label}
            d={roundedBar(at, PROP_BOX.top, at + width, PROP_BOX.bar)}
            fill={slotColor(part.slot)}
          >
            <title>{part.title}</title>
          </path>
        ) : (
          <rect
            key={part.label}
            x={n(at)}
            y={PROP_BOX.top}
            width={n(width)}
            height={PROP_BOX.bar}
            fill={slotColor(part.slot)}
          >
            <title>{part.title}</title>
          </rect>
        ),
      )}

      {drawn.map(({ part, at, width }) =>
        part.inside === undefined || width < PROP_LABEL_FLOOR ? null : (
          <text
            className="lbl"
            key={`${part.label}-label`}
            x={n(at + 10)}
            y={PROP_BOX.top + 18}
            fill="var(--surface)"
          >
            {part.inside}
          </text>
        ),
      )}

      <line
        className="axisline"
        x1={PROP_BOX.left}
        y1={PROP_BOX.top + PROP_BOX.bar + 6}
        x2={PROP_BOX.right}
        y2={PROP_BOX.top + PROP_BOX.bar + 6}
      />
      <text className="ax" x={PROP_BOX.left} y={PROP_BOX.top + PROP_BOX.bar + 20}>
        {startLabel}
      </text>
      <text className="ax" x={PROP_BOX.right} y={PROP_BOX.top + PROP_BOX.bar + 20} textAnchor="end">
        {endLabel}
      </text>
    </>
  )
}

/* -------------------------------------------------------------- the zero line */

export type ZeroLineProps = {
  readonly y: number
  readonly x1?: number | undefined
  readonly x2?: number | undefined
}

/**
 * `.zero` — the baseline a chart with negative values needs, drawn at the value
 * rather than at the bottom of the plot. `studio/analytics.html`'s MRR movements
 * is the one chart in the set with a bar below it (churn).
 */
export function ZeroLine({ y, x1, x2 }: ZeroLineProps) {
  return (
    <line
      className="zero"
      x1={x1 ?? COLUMN_BOX.left}
      y1={n(y)}
      x2={x2 ?? COLUMN_BOX.right}
      y2={n(y)}
    />
  )
}

/** Re-exported so a story can put a `.chart .lbl` where the shapes do not. */
export type ChartTextProps = Common & {
  readonly x: number
  readonly y: number
  readonly kind: 'ax' | 'lbl'
  readonly anchor?: 'start' | 'middle' | 'end' | undefined
  readonly children: ReactNode
}

export function ChartText({ x, y, kind, anchor, children, ...common }: ChartTextProps) {
  return (
    <text className={cx(kind)} x={n(x)} y={n(y)} textAnchor={anchor} {...common}>
      {children}
    </text>
  )
}

/**
 * A rounded axis from zero to a whole number of steps above the busiest value.
 *
 * Five ticks from zero to a round ceiling above the busiest day, because that is what
 * `widget-chatbot.html` draws (0 · 20 · 40 · 60 · 80 over a peak of 69). The ceiling
 * is a whole number of steps at or above the peak, so the tallest column never
 * touches the top of the plot.
 *
 * It lives here rather than beside the one chart that first needed it: two callers
 * (session 19's widget page and its report overview) asked the same question, and an
 * axis is chart geometry.
 *
 * Written as a list of steps and a loop rather than `Math.ceil(peak / step) * step`,
 * because `Math.ceil` is lint-banned outside `money.ts` and `dates.ts`
 * (`eslint.config.js`) — and a list of the round numbers an axis is allowed to use is
 * more honest than a logarithm anyway.
 *
 * **`label` is required, and it is `format.number`.** An axis label is a rendered
 * number and every rendered number goes through `useFormat()` (`CLAUDE.md`): with
 * `String(value)` as a default, the report's overview chart printed
 * `15000 / 10000 / 5000 / 0` up the side of a plot whose own end label read `14,280`.
 * A default is what let that happen, so there is none — this function cannot format,
 * because it is not a component and has no locale.
 */
export function roundAxisTicks(
  peak: number,
  label: (value: number) => string,
): readonly AxisTick[] {
  const step = tickStep(peak)
  let top = step
  while (top < peak) top += step
  const ticks: AxisTick[] = []
  for (let value = top; value >= 0; value -= step) ticks.push({ value, label: label(value) })
  return ticks
}

/** The round numbers an axis step may be. Four of them has to reach the peak. */
const STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1_000, 2_500, 5_000, 10_000] as const

/** The smallest step whose four intervals cover the peak. */
function tickStep(peak: number): number {
  const found = STEPS.find((step) => step * 4 >= peak)
  if (found !== undefined) return found
  // Past the list, keep doubling the largest one rather than drawing a broken axis.
  let step: number = STEPS[STEPS.length - 1] ?? 1
  while (step * 4 < peak) step *= 2
  return step
}
